import { create } from 'zustand';
import { HelpDeskTab, API_BASE_URL } from '../lib/api';
import { useAuthStore } from './auth-store';

// Re-export the HelpDeskTab type for convenience
export type { HelpDeskTab } from '../lib/api';

export interface HelpDeskTicket {
  id: number;
  ticket_id: string;
  reference: string;
  status: string;
  assign_to: string;
  help_desk_ticket_url: string;
  help_desk_magento_url: string;
  date: string;
  created_at: string;
  updated_at: string;
}

interface HelpDeskState {
  activeTab: HelpDeskTab;
  items: HelpDeskTicket[];
  page: number;
  limit: number;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  total: number;
}

interface HelpDeskActions {
  setActiveTab: (tab: HelpDeskTab) => void;
  fetchTickets: () => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  clearError: () => void;
}

export type HelpDeskStore = HelpDeskState & HelpDeskActions;

export const useHelpDeskStore = create<HelpDeskStore>((set, get) => ({
  // Initial state
  activeTab: 'all-tickets',
  items: [],
  page: 1,
  limit: 10,
  isLoading: false,
  error: null,
  hasMore: false,
  total: 0,

  // Actions
  setActiveTab: (tab: HelpDeskTab) => {
    set({ activeTab: tab, page: 1, items: [] });
    get().fetchTickets();
  },

  fetchTickets: async () => {
    const { activeTab, page, limit } = get();
    const authState = useAuthStore.getState();
    
    if (!authState.token) {
      set({ error: 'No authentication token available' });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      // Make the API call directly to get the full response structure
      const url = `${API_BASE_URL}/helpdesk?tab=${activeTab}&page=${page}&limit=${limit}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${authState.token}`,
        },
      });

      const responseData = await response.json();
    //   console.log('HelpDesk Raw API Response:', responseData); // Debug log

      if (!response.ok) {
        throw new Error(responseData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      if (!responseData.success) {
        throw new Error(responseData.message || 'Failed to fetch help desk tickets');
      }

      // Extract data and meta from the response
      const tickets = responseData.data || [];
      const meta = responseData.meta || {};
      
    //   console.log('HelpDesk Tickets:', tickets); // Debug log
    //   console.log('HelpDesk Meta:', meta); // Debug log
      
      set({
        items: page === 1 ? tickets : [...get().items, ...tickets],
        total: meta.total || 0,
        hasMore: meta.has_more || false,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('HelpDesk API Error:', error); // Debug log
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch help desk tickets';
      set({
        isLoading: false,
        error: errorMessage,
      });
    }
  },

  loadMore: async () => {
    const { hasMore, isLoading } = get();
    
    if (!hasMore || isLoading) return;

    set((state) => ({ page: state.page + 1 }));
    await get().fetchTickets();
  },

  refresh: async () => {
    set({ page: 1, items: [] });
    await get().fetchTickets();
  },

  clearError: () => {
    set({ error: null });
  },
}));
